import {
  HomeWorkOutlined,
  MedicalServicesOutlined,
  SavingsOutlined,
  SchoolOutlined,
  ShieldOutlined,
  SvgIconComponent,
} from '@mui/icons-material'
import { CreditProductName } from '@/types/customer-context'

const rcpQuotePath = '/accounting/insurance/rcp-medicos'

export type CreditShowcaseProductKey = CreditProductName | 'home_equity' | 'rcp'

export type CreditShowcaseAction =
  | { kind: 'application'; product: CreditProductName }
  | { kind: 'lead' }
  | { kind: 'route'; path: string }

export type CreditShowcaseProduct = {
  key: CreditShowcaseProductKey
  name: string
  icon: SvgIconComponent
  description: string
  /** Semestre mínimo para o estudante. Sem valor, o produto não trava por semestre. */
  minSemester?: number
  facts: [string, string][]
  needs: string[]
  steps: string[]
  action: CreditShowcaseAction
}

export const creditProducts: Record<
  CreditShowcaseProductKey,
  CreditShowcaseProduct
> = {
  allowance: {
    key: 'allowance',
    name: 'Mesada',
    icon: SavingsOutlined,
    description:
      'Receba um valor mensal para ajudar nos custos de vida durante o internato e pague quando concluir essa etapa.',
    minSemester: 9,
    facts: [
      ['Para quem', 'A partir do 9º semestre'],
      ['Como funciona', 'Valor mensal no internato'],
      ['Pagamento', 'Depois do internato'],
    ],
    needs: ['Dados da faculdade e do curso', 'Seus dados financeiros'],
    steps: [
      'Faculdade',
      'Simulação',
      'Seus dados',
      'Dados financeiros',
      'Revisão',
    ],
    action: { kind: 'application', product: 'allowance' },
  },
  internship: {
    key: 'internship',
    name: 'Financiamento Estudantil',
    icon: SchoolOutlined,
    description: 'Reduza suas mensalidades enquanto estiver no internato.',
    minSemester: 8,
    facts: [
      ['Para quem', 'A partir do 9º semestre'],
      ['Como funciona', 'Mensalidades menores no internato'],
      ['Garantia', 'Até 2 responsáveis'],
    ],
    needs: [
      'Dados da faculdade e da mensalidade',
      'Até 2 responsáveis ou garantidores',
      'Autorização de consulta ao SCR',
    ],
    steps: [
      'Faculdade',
      'Simulação',
      'Dados do estudante',
      'Responsáveis',
      'Autorização SCR',
    ],
    action: { kind: 'application', product: 'internship' },
  },
  personal_loan: {
    key: 'personal_loan',
    name: 'Crédito para médicos',
    icon: MedicalServicesOutlined,
    description:
      'Empréstimos de até R$ 40 mil para facilitar sua jornada profissional.',
    facts: [
      ['Valor', 'De R$ 5 mil a R$ 40 mil'],
      ['Prazo', 'De 6 a 24 meses'],
      ['Juros', 'A partir de 4,49% a.m.'],
    ],
    needs: [
      'CRM ativo',
      'Dados profissionais e de renda',
      'Conta bancária para receber',
    ],
    steps: [
      'Simulação',
      'Dados pessoais e formação',
      'Renda e conta para receber',
      'Documentos',
      'Análise e contrato',
    ],
    action: { kind: 'application', product: 'personal_loan' },
  },
  home_equity: {
    key: 'home_equity',
    name: 'Crédito com imóvel em garantia',
    icon: HomeWorkOutlined,
    description: 'Empréstimos a partir de R$ 40 mil para quem possui imóvel.',
    facts: [
      ['Valor', 'A partir de R$ 40 mil'],
      ['Juros', 'A partir de 1,09% a.m. + IPCA'],
      ['Imóveis', 'Sul, Sudeste e Centro-Oeste'],
    ],
    needs: [
      'Matrícula e IPTU do imóvel',
      'Comprovante de renda',
      'Autorização de consulta ao SCR',
    ],
    steps: [
      'Valor desejado',
      'Dados do imóvel',
      'Dados pessoais',
      'Documentos',
      'Autorização SCR',
    ],
    action: { kind: 'lead' },
  },
  rcp: {
    key: 'rcp',
    name: 'Seguro RCP para médicos',
    icon: ShieldOutlined,
    description:
      'Seguro de Responsabilidade Civil Profissional para proteger sua atuação médica.',
    facts: [
      ['Para quem', 'Médicos com CRM ativo'],
      ['Cobertura', 'Responsabilidade civil profissional'],
      ['Contratação', 'Cotação pelo app'],
    ],
    needs: ['CRM e especialidade', 'Dados da sua atuação'],
    steps: ['Cotação', 'Proposta', 'Contratação'],
    action: { kind: 'route', path: rcpQuotePath },
  },
}

export const creditProductKeys = Object.keys(
  creditProducts,
) as CreditShowcaseProductKey[]

export function isCreditShowcaseProductKey(
  value: string,
): value is CreditShowcaseProductKey {
  return value in creditProducts
}
